/* JB-PDP-CONFIGURATOR-V1:2026-08-14 */
(() => {
  class JbPdpConfigurator extends HTMLElement {
    static jbNoAutoScroll() {}

    connectedCallback() {
      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    start() {
      this.stop();
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.frame = 0;
      this.syncTimer = 0;
      this.rehydrateTimer = 0;
      this.resolveReferences();
      this.bindEvents();
      this.observeRerenders();
      this.syncPairedMetalSwitch();
      this.syncInitialPieceOrder();
      this.neutralizeGalleryAutoScroll();
      this.sync();
    }

    stop() {
      this.abortController?.abort();
      this.galleryObserver?.disconnect();
      this.mutationObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.syncTimer);
      clearTimeout(this.rehydrateTimer);
      this.galleryObserver = null;
      this.mutationObserver = null;
    }

    resolveReferences() {
      this.headerSectionElement = undefined;
      this.gallery = this.querySelector('product-gallery.product-gallery--focused-variant');
      this.standardGallery = this.gallery ? null : this.querySelector('product-gallery');
      this.carousel = this.activeGallery()?.querySelector('.product-gallery__carousel');
      this.tray = this.querySelector('[data-jb-preview]');
      this.previewImage = this.querySelector('[data-jb-preview-image]');
      this.previewButton = this.querySelector('[data-jb-preview-button]');
      this.summary = this.querySelector('[data-jb-preview-summary]');
      this.cartSummary = this.querySelector('[data-jb-cart-summary]');
      this.trayAtc = this.querySelector('[data-jb-tray-atc]');
      this.startBoundary = this.querySelector('[data-jb-configurator-start]');
      this.endBoundary = this.querySelector('[data-jb-configurator-end]');
      this.rerenderHost = this.closest('product-rerender[observe-form]');
      this.formId = this.gallery?.getAttribute('form') || this.rerenderHost?.getAttribute('observe-form');
      this.form = this.formId ? document.forms.namedItem(this.formId) : null;

      const hasRealMedia = Boolean(this.carousel?.querySelector('.product-gallery__media[data-media-id]:not([data-media-id=""]) img'));
      const trayReady = Boolean(this.activeGallery() && this.carousel && hasRealMedia && this.tray && this.startBoundary && this.endBoundary && this.form);
      this.dataset.jbFocusedGallery = this.gallery && trayReady ? 'true' : 'false';
      this.dataset.jbTrayEnabled = trayReady ? 'true' : 'false';

      if (!trayReady) {
        this.tray?.setAttribute('hidden', '');
        return;
      }

      this.galleryObserver = new IntersectionObserver(
        () => this.updateTrayVisibility(),
        { threshold: 0.01 }
      );
      this.galleryObserver.observe(this.activeGallery());
    }

    activeGallery() {
      return this.gallery || this.standardGallery || null;
    }

    currentMediaCell() {
      if (this.gallery) return this.gallery.querySelector('.product-gallery__media--piece:not([hidden])');
      if (!this.carousel) return null;
      return [...this.carousel.querySelectorAll('.product-gallery__media:not([hidden])')].find((cell) => cell.style.order === '-1')
        || this.carousel.querySelector('.product-gallery__media.is-initial:not([hidden])')
        || this.carousel.querySelector('.product-gallery__media:not([hidden])');
    }

    bindEvents() {
      if (!this.signal) return;
      this.form?.addEventListener('variant:change', (event) => {
        if (event.detail?.variant) this.lastVariantHadMedia = Boolean(event.detail.variant.featured_media);
        this.pieceOrderMediaId = event.detail?.variant?.featured_media?.id ?? this.pieceOrderMediaId;
        this.scrollAnchor = { y: window.scrollY, until: Date.now() + 1200 };
        this.applyStoredPieceOrder();
        this.neutralizeGalleryAutoScroll();
        this.syncPairedMetalSwitch();
        this.scheduleSync();
      }, { signal: this.signal });
      const cancelAnchor = () => { this.scrollAnchor = null; };
      window.addEventListener('wheel', cancelAnchor, { passive: true, signal: this.signal });
      window.addEventListener('touchstart', cancelAnchor, { passive: true, signal: this.signal });
      window.addEventListener('keydown', cancelAnchor, { signal: this.signal });
      this.form?.addEventListener('product:rerender', (event) => this.syncGalleryModeFromFragment(event), { signal: this.signal });
      this.carousel?.addEventListener('carousel:change', () => this.scheduleSync(), { signal: this.signal });
      this.carousel?.addEventListener('carousel:settle', () => this.sync(), { signal: this.signal });
      this.previewButton?.addEventListener('click', () => this.openSelectedPiece(), { signal: this.signal });
      window.addEventListener('scroll', () => this.requestBoundaryCheck(), { passive: true, signal: this.signal });
      window.addEventListener('resize', () => this.scheduleSync(), { signal: this.signal });
    }

    observeRerenders() {
      this.mutationObserver = new MutationObserver((records) => {
        if (records.some((record) => [...record.addedNodes, ...record.removedNodes].some((node) => this.isNativeStructureNode(node)))) {
          this.queueRehydrate();
        }
      });
      this.mutationObserver.observe(this, { childList: true, subtree: true });
    }

    isNativeStructureNode(node) {
      if (node.nodeType !== Node.ELEMENT_NODE) return false;
      const selector = '[data-block-type="variant-picker"], variant-picker, product-gallery';
      return node.matches(selector) || Boolean(node.querySelector(selector));
    }

    queueRehydrate() {
      clearTimeout(this.rehydrateTimer);
      this.rehydrateTimer = window.setTimeout(() => this.start(), 0);
    }

    syncPairedMetalSwitch() {
      const switchRoot = this.querySelector('[data-jb-paired-metal-switch]');
      const siblingLink = switchRoot?.querySelector('[data-jb-paired-metal-link]');
      if (!siblingLink) return;

      const disable = () => {
        siblingLink.removeAttribute('href');
        siblingLink.setAttribute('aria-disabled', 'true');
        siblingLink.classList.add('is-disabled');
      };
      disable();

      const mappingScript = switchRoot.querySelector('script[data-jb-paired-metal-option-orders]');
      const variantScript = this.querySelector('variant-picker script[data-variant]');
      if (!mappingScript || !variantScript) return;

      let mapping;
      let variant;
      try {
        mapping = JSON.parse(mappingScript.textContent);
        variant = JSON.parse(variantScript.textContent);
      } catch (error) {
        disable();
        return;
      }

      const selectedLabels = Array.isArray(variant.options)
        ? variant.options
        : [variant.option1, variant.option2, variant.option3].filter((value) => value != null);
      const sourceValues = mapping?.source?.optionValues;
      const siblingValues = mapping?.sibling?.optionValues;
      const siblingVariants = mapping?.sibling?.variants;
      const siblingUrl = siblingLink.getAttribute('data-jb-paired-metal-url');

      if (!Array.isArray(selectedLabels) || selectedLabels.length !== 3
        || !Array.isArray(sourceValues) || sourceValues.length !== 3
        || !Array.isArray(siblingValues) || siblingValues.length !== 3
        || !Array.isArray(siblingVariants) || !siblingUrl) {
        disable();
        return;
      }

      const siblingLabels = [];
      for (let axis = 0; axis < 3; axis += 1) {
        const sourceAxisValues = sourceValues[axis];
        const siblingAxisValues = siblingValues[axis];
        const sourceIndex = Array.isArray(sourceAxisValues) ? sourceAxisValues.indexOf(selectedLabels[axis]) : -1;
        if (sourceIndex < 0 || !Array.isArray(siblingAxisValues) || siblingAxisValues[sourceIndex] == null) {
          disable();
          return;
        }
        siblingLabels.push(siblingAxisValues[sourceIndex]);
      }

      const siblingVariant = siblingVariants.find((candidate) => Array.isArray(candidate.options)
        && candidate.options.length === 3
        && candidate.options.every((value, axis) => value === siblingLabels[axis]));
      if (!siblingVariant?.id) {
        disable();
        return;
      }

      siblingLink.setAttribute('href', `${siblingUrl}?variant=${encodeURIComponent(siblingVariant.id)}`);
      siblingLink.removeAttribute('aria-disabled');
      siblingLink.classList.remove('is-disabled');
    }

    scheduleSync() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.syncTimer);
      this.frame = requestAnimationFrame(() => {
        this.syncTimer = window.setTimeout(() => this.sync(), 0);
      });
    }

    requestBoundaryCheck() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.updateTrayVisibility());
    }

    stickyAreaHeight() {
      const rootStyles = getComputedStyle(document.documentElement);
      const numeric = (name, fallback = 0) => {
        const parsed = Number.parseFloat(rootStyles.getPropertyValue(name));
        return Number.isNaN(parsed) ? fallback : parsed;
      };
      return numeric('--announcement-bar-is-sticky') * numeric('--announcement-bar-height')
        + numeric('--header-is-sticky') * numeric('--header-is-visible', 1) * numeric('--header-height');
    }

    headerSection() {
      if (this.headerSectionElement === undefined) {
        this.headerSectionElement = document.querySelector('header')?.closest('.shopify-section') || null;
      }
      return this.headerSectionElement;
    }

    stickyBottom() {
      const header = this.headerSection();
      if (header) {
        const rect = header.getBoundingClientRect();
        if (rect.height > 0) return Math.max(0, rect.bottom);
      }
      return this.stickyAreaHeight();
    }

    updateTrayVisibility() {
      if (!this.tray || !this.activeGallery() || !this.startBoundary || !this.endBoundary) return;
      if (this.dataset.jbTrayEnabled !== 'true') return;
      const offset = this.stickyBottom();
      const offsetPx = `${offset}px`;
      if (this.tray.style.top !== offsetPx) this.tray.style.top = offsetPx;
      const mainMedia = this.currentMediaCell() || this.activeGallery();
      const mediaRect = mainMedia.getBoundingClientRect();
      const endRect = this.endBoundary.getBoundingClientRect();
      const threshold = window.matchMedia('(min-width: 740px)').matches ? 2 / 3 : 1 / 2;
      const mainImageMostlyLeft = mediaRect.top + mediaRect.height * threshold <= offset;
      const pastPurchaseEnd = endRect.top <= offset;
      const shouldShow = mainImageMostlyLeft;
      this.tray.toggleAttribute('hidden', !shouldShow);
      this.tray.dataset.jbTrayMode = pastPurchaseEnd ? 'cart' : 'details';
      if (pastPurchaseEnd) this.syncCartButton();
      if (shouldShow && this.previewImage) this.previewImage.loading = 'eager';
    }

    syncCartButton() {
      if (!this.trayAtc) return;
      const nativeSubmit = this.querySelector('buy-buttons button[type="submit"], buy-buttons [type="submit"]');
      if (!nativeSubmit) return;
      this.trayAtc.disabled = nativeSubmit.disabled;
      const label = nativeSubmit.textContent.trim() || 'Add to cart';
      const price = nativeSubmit.disabled ? '' : this.currentPriceText();
      const composed = price ? `${label} · ${price}` : label;
      if (this.trayAtc.textContent.trim() !== composed) this.trayAtc.textContent = composed;
    }

    currentPriceText() {
      const priceElement = this.querySelector('[data-block-type="price"] sale-price');
      if (!priceElement) return '';
      const clone = priceElement.cloneNode(true);
      clone.querySelectorAll('.sr-only').forEach((node) => node.remove());
      return clone.textContent.replace(/\s+/g, ' ').trim();
    }

    hasPendingPhotography() {
      if (this.gallery) return this.gallery.classList.contains('product-gallery--representative-stone');
      if (this.standardGallery) return this.lastVariantHadMedia === false;
      return false;
    }

    neutralizeGalleryAutoScroll() {
      this.querySelectorAll('product-gallery .product-gallery__media').forEach((cell) => {
        if (cell.scrollIntoView !== JbPdpConfigurator.jbNoAutoScroll) cell.scrollIntoView = JbPdpConfigurator.jbNoAutoScroll;
      });
    }

    standardGalleryCarousel() {
      const gallery = this.querySelector('product-gallery');
      if (!gallery || gallery.classList.contains('product-gallery--focused-variant')) return null;
      return gallery.querySelector('.product-gallery__carousel');
    }

    syncInitialPieceOrder() {
      const carousel = this.standardGalleryCarousel();
      const initial = carousel?.querySelector('.product-gallery__media.is-initial');
      if (!initial) return;
      if (this.pieceOrderMediaId == null && initial.dataset.mediaId) this.pieceOrderMediaId = initial.dataset.mediaId;
      this.applyStoredPieceOrder();
      this.resetStandardCarouselScroll();
    }

    /* JB-PHOTO-FALLBACK-V1: the theme scrolls to the chosen photo's place in the list before CSS moves that photo
       to the front, which left phones showing the next photo (often the lifestyle shot). Put the carousel back on
       the first slot, where the chosen photo now is. */
    resetStandardCarouselScroll() {
      const carousel = this.standardGalleryCarousel();
      if (!carousel) return;
      let touched = false;
      const markTouched = () => { touched = true; };
      carousel.addEventListener('pointerdown', markTouched, { once: true, signal: this.signal });
      const reset = () => {
        if (!touched && carousel.isConnected && carousel.scrollLeft !== 0) carousel.scrollTo({ left: 0, behavior: 'instant' });
      };
      customElements.whenDefined('scroll-carousel').then(() => {
        requestAnimationFrame(reset);
        setTimeout(reset, 300);
      });
    }

    applyStoredPieceOrder() {
      const carousel = this.standardGalleryCarousel();
      const mediaId = this.pieceOrderMediaId;
      if (!carousel || mediaId == null) return;
      carousel.querySelectorAll('.product-gallery__media').forEach((cell) => {
        const isPiece = String(mediaId) === cell.dataset.mediaId;
        cell.style.order = isPiece ? '-1' : '';
        cell.toggleAttribute('data-jb-piece', isPiece); /* JB-PHOTO-FALLBACK-V1: greyed when the gallery is a stand-in */
      });
    }

    deepQuery(root, selector) {
      if (!root?.querySelectorAll) return null;
      const direct = root.querySelector(selector);
      if (direct) return direct;
      for (const template of root.querySelectorAll('template')) {
        const nested = this.deepQuery(template.content, selector);
        if (nested) return nested;
      }
      return null;
    }

    syncGalleryModeFromFragment(event) {
      const fragment = event.detail?.htmlFragment;
      if (!fragment) return;
      this.restoreScrollAnchor();
      const currentGallery = this.activeGallery();
      if (currentGallery) {
        const formId = currentGallery.getAttribute('form');
        const nextGallery = formId ? this.deepQuery(fragment, `product-gallery[form="${formId}"]`) : null;
        if (nextGallery) {
          currentGallery.classList.toggle('product-gallery--representative-stone', nextGallery.classList.contains('product-gallery--representative-stone'));
          if (this.standardGallery) {
            /* JB-PHOTO-FALLBACK-V1: lead with the server's chosen photo, which is the stand-in when the variant has none */
            const nextInitial = nextGallery.querySelector('.product-gallery__media.is-initial[data-media-id]');
            if (nextInitial?.dataset.mediaId) {
              this.pieceOrderMediaId = nextInitial.dataset.mediaId;
              const cell = this.carousel?.querySelector(`.product-gallery__media[data-media-id="${nextInitial.dataset.mediaId}"]`);
              if (cell?.hidden) cell.hidden = false;
              this.applyStoredPieceOrder();
              this.resetStandardCarouselScroll();
            }
          }

          const currentNote = currentGallery.querySelector('.jb-product-gallery-note');
          const nextNote = nextGallery.querySelector('.jb-product-gallery-note');
          currentNote?.remove();
          if (nextNote) {
            const carouselWrapper = currentGallery.querySelector('.product-gallery__carousel')?.parentElement;
            carouselWrapper?.insertAdjacentElement('afterend', nextNote.cloneNode(true));
          }
        }
      }
      this.scheduleSync();
    }

    selectedOptions() {
      const seenGroups = new Set();
      return [...this.querySelectorAll('[data-jb-option-group]')]
        .map((group) => {
          const groupName = group.dataset.jbOptionGroup;
          if (!groupName || seenGroups.has(groupName)) return null;
          seenGroups.add(groupName);
          const selected = group.querySelector('input[data-jb-option-label]:checked')
            || group.querySelector('a[data-jb-option-label].is-selected')
            || group.querySelector('button[data-jb-option-label].is-selected');
          return selected?.dataset.jbOptionLabel || null;
        })
        .filter(Boolean);
    }

    syncSummary() {
      const optionText = this.selectedOptions().join(' · ');
      if (this.summary) this.summary.textContent = optionText;
      if (this.cartSummary) this.cartSummary.textContent = optionText;
      const productTitle = this.dataset.jbProductTitle || 'product';
      this.previewButton?.setAttribute('aria-label', `Enlarge ${productTitle}${optionText ? ` in ${optionText}` : ''}`);
    }

    selectedPieceImage() {
      return this.currentMediaCell()?.querySelector('img') || null;
    }

    syncImage() {
      this.dataset.jbPendingPhoto = this.hasPendingPhotography() ? 'true' : 'false';
      const source = this.selectedPieceImage();
      if (!source || !this.previewImage) return;
      const sourceValue = source.getAttribute('src') || source.currentSrc;
      if (sourceValue && this.previewImage.getAttribute('src') !== sourceValue) this.previewImage.setAttribute('src', sourceValue);
      ['srcset', 'sizes'].forEach((attribute) => {
        const value = source.getAttribute(attribute);
        if (value) this.previewImage.setAttribute(attribute, value);
        else this.previewImage.removeAttribute(attribute);
      });
      this.previewImage.loading = this.tray?.hidden ? 'lazy' : 'eager';
    }

    stabilizeCarouselHeight() {
      if (!this.standardGallery || !this.carousel) return;
      const cells = [...this.carousel.querySelectorAll('.product-gallery__media')];
      const visibleCell = cells.find((cell) => !cell.hidden);
      const cellWidth = visibleCell?.getBoundingClientRect().width || this.carousel.clientWidth;
      if (!cellWidth) return;
      let tallest = 0;
      for (const cell of cells) {
        const img = cell.querySelector('img');
        const width = Number.parseFloat(img?.getAttribute('width'));
        const height = Number.parseFloat(img?.getAttribute('height'));
        if (width > 0 && height > 0) tallest = Math.max(tallest, (cellWidth * height) / width);
      }
      if (!tallest) return;
      const value = `${Math.ceil(tallest)}px`;
      if (this.carousel.style.minBlockSize !== value) this.carousel.style.minBlockSize = value;
    }

    restoreScrollAnchor() {
      const anchor = this.scrollAnchor;
      if (!anchor) return;
      if (Date.now() > anchor.until) {
        this.scrollAnchor = null;
        return;
      }
      if (Math.abs(window.scrollY - anchor.y) > 0.5) window.scrollTo(0, anchor.y);
    }

    sync() {
      this.applyStoredPieceOrder();
      this.neutralizeGalleryAutoScroll();
      this.restoreScrollAnchor();
      this.stabilizeCarouselHeight();
      this.syncSummary();
      this.syncImage();
      this.updateTrayVisibility();
    }

    openSelectedPiece() {
      const gallery = this.activeGallery();
      if (!gallery || this.hasPendingPhotography()) return;
      const images = [...gallery.querySelectorAll('.product-gallery__media[data-media-type="image"]:not([hidden])')];
      const piece = this.currentMediaCell();
      const index = images.indexOf(piece);
      if (index >= 0) gallery.dispatchEvent(new CustomEvent('lightbox:open', { bubbles: true, detail: { index } }));
    }
  }

  if (!customElements.get('jb-pdp-configurator')) customElements.define('jb-pdp-configurator', JbPdpConfigurator);
})();

/* JB-GEMSTONE-TOOLTIPS-V1:2026-09-26 */
(() => {
  if (document.querySelector('[data-jb-gemstone-tooltip]')) return;
  const selector = '.product-info .variant-picker .color-swatch[data-jb-option-label]';
  const tooltip = document.createElement('div');
  tooltip.className = 'jb-gemstone-tooltip';
  tooltip.dataset.jbGemstoneTooltip = '';
  // Names are already exposed by the radio labels; this is a visual enhancement.
  tooltip.setAttribute('aria-hidden', 'true');
  tooltip.hidden = true;
  const name = document.createElement('span');
  name.className = 'jb-gemstone-tooltip__name';
  tooltip.append(name);
  document.body.append(tooltip);
  let activeLabel = null;
  let hideTimer;
  const hide = () => {
    clearTimeout(hideTimer);
    tooltip.hidden = true;
    activeLabel = null;
  };
  const scheduleHide = () => {
    clearTimeout(hideTimer);
    hideTimer = setTimeout(hide, 120);
  };
  const show = (label) => {
    clearTimeout(hideTimer);
    const value = label?.dataset.jbOptionLabel;
    if (!value || !label.isConnected) return hide();
    activeLabel = label;
    name.textContent = label.classList.contains("is-disabled") ? `${value} — Sold out` : value;
    tooltip.hidden = false;
    const rect = label.getBoundingClientRect();
    const box = tooltip.getBoundingClientRect();
    const width = document.documentElement.clientWidth;
    const height = document.documentElement.clientHeight;
    const left = Math.max(12, Math.min(rect.left + rect.width / 2 - box.width / 2, width - box.width - 12));
    const above = rect.top - box.height - 8;
    const top = above >= 12 ? above : Math.min(rect.bottom + 8, height - box.height - 12);
    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${Math.max(12, top)}px`;
  };
  document.addEventListener('pointerover', (event) => {
    if (event.pointerType === 'touch' || !window.matchMedia('(hover: hover)').matches) return;
    const label = event.target.closest?.(selector);
    if (label && !label.contains(event.relatedTarget)) show(label);
  });
  document.addEventListener('pointerout', (event) => {
    if (activeLabel?.contains(event.target) && !activeLabel.contains(event.relatedTarget)) scheduleHide();
  });
  tooltip.addEventListener('pointerenter', () => clearTimeout(hideTimer));
  tooltip.addEventListener('pointerleave', scheduleHide);
  document.addEventListener('focusin', (event) => {
    const input = event.target;
    const label = input.matches?.('input[type="radio"]:focus-visible') ? input.labels?.[0] : null;
    if (label?.matches(selector)) show(label);
    else hide();
  });
  document.addEventListener('focusout', (event) => {
    if (event.target.labels?.[0] === activeLabel) hide();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') hide();
  });
  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'touch' || (!event.target.closest?.(selector) && !tooltip.contains(event.target))) hide();
  });
  document.addEventListener('scroll', () => {
    if (activeLabel && document.activeElement?.matches('input[type="radio"]:focus-visible') && document.activeElement.labels?.[0] === activeLabel) show(activeLabel);
    else hide();
  }, { capture: true, passive: true });
  window.addEventListener('resize', hide, { passive: true });
})();



/* JB-MOBILE-GEMSTONE-PICKER-V1:2026-09-26 */
(() => {
  if (document.querySelector('[data-jb-gemstone-sheet]')) return;
  const groupSelector = '.product-info .variant-picker fieldset[data-jb-option-group="Gemstone"]';
  const mobile = window.matchMedia('(max-width: 699px)');
  const dialog = document.createElement('dialog');
  if (typeof dialog.showModal !== 'function') return; // Keep the original picker as the fallback.
  dialog.className = 'jb-gemstone-sheet';
  dialog.dataset.jbGemstoneSheet = '';
  dialog.setAttribute('aria-label', 'Choose gemstone');
  const header = document.createElement('div');
  header.className = 'jb-gemstone-sheet__header';
  const heading = document.createElement('h2');
  heading.className = 'jb-gemstone-sheet__title';
  heading.textContent = 'Choose gemstone';
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'jb-gemstone-sheet__close';
  close.setAttribute('aria-label', 'Close gemstone picker');
  close.textContent = '×';
  const grid = document.createElement('div');
  grid.className = 'jb-gemstone-sheet__grid';
  header.append(heading, close);
  dialog.append(header, grid);
  document.body.append(dialog);
  let ownerName = '';
  let frame = 0;
  const groups = () => [...document.querySelectorAll(groupSelector)];
  const owner = () => groups().find(group => group.querySelector('input[type="radio"]')?.name === ownerName);
  const swatch = (label) => label?.style.getPropertyValue('--swatch-background') || '';
  const chip = (label) => {
    const element = document.createElement('span');
    element.className = 'jb-gemstone-chip';
    element.setAttribute('aria-hidden', 'true');
    element.style.setProperty('--swatch-background', swatch(label));
    return element;
  };
  function sync() {
    frame = 0;
    for (const group of groups()) {
      const input = group.querySelector('input[type="radio"]:checked');
      const label = input?.labels?.[0];
      if (!label?.matches('.color-swatch[data-jb-option-label]')) continue;
      let trigger = group.querySelector('.jb-gemstone-chooser');
      if (!trigger) {
        trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'jb-gemstone-chooser';
        trigger.setAttribute('aria-haspopup', 'dialog');
        const name = document.createElement('span');
        name.className = 'jb-gemstone-chooser__name';
        const action = document.createElement('span');
        action.className = 'jb-gemstone-chooser__action';
        action.textContent = 'Change';
        trigger.append(chip(label), name, action);
        group.append(trigger);
        group.classList.add('jb-gemstone-compact');
      }
      const name = label.dataset.jbOptionLabel;
      const unavailable = label.classList.contains("is-disabled") || input.disabled;
      const text = `Gemstone: ${name}${unavailable ? " — Sold out" : ""}`;
      const caption = trigger.querySelector('.jb-gemstone-chooser__name');
      if (caption.textContent !== text) caption.textContent = text;
      trigger.setAttribute('aria-label', `Choose gemstone, current selection: ${name}${unavailable ? ", Sold out" : ""}`);
      const image = trigger.querySelector('.jb-gemstone-chip');
      if (image.style.getPropertyValue('--swatch-background') !== swatch(label)) image.style.setProperty('--swatch-background', swatch(label));
    }
  }
  const scheduleSync = () => { if (!frame) frame = requestAnimationFrame(sync); };
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('.jb-gemstone-chooser');
    if (!trigger || !mobile.matches) return;
    const group = trigger.closest(groupSelector);
    ownerName = group.querySelector('input[type="radio"]').name;
    grid.replaceChildren();
    for (const input of group.querySelectorAll('input[type="radio"]')) {
      const label = input.labels?.[0];
      if (!label) continue;
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'jb-gemstone-sheet__option';
      option.dataset.value = input.value;
      option.disabled = input.disabled;
      const unavailable = input.disabled || label.classList.contains("is-disabled");
      option.dataset.unavailable = String(unavailable);
      option.setAttribute('aria-pressed', String(input.checked));
      const name = document.createElement('span');
      name.textContent = label.dataset.jbOptionLabel || label.textContent.trim();
      option.append(chip(label), name);
      if (unavailable) {
        const status = document.createElement('span');
        status.className = 'jb-gemstone-sheet__availability';
        status.textContent = 'Sold out';
        option.append(status);
        option.setAttribute('aria-label', `${name.textContent}, Sold out`);
      }
      grid.append(option);
    }
    document.querySelector('.jb-gemstone-tooltip')?.setAttribute('hidden', '');
    dialog.showModal();
    document.documentElement.classList.add('jb-gem-sheet-open');
    grid.scrollTop = 0;
    close.focus({ preventScroll: true });
  });
  grid.addEventListener('click', (event) => {
    const option = event.target.closest('.jb-gemstone-sheet__option');
    if (!option || option.disabled) return;
    const input = [...(owner()?.querySelectorAll('input[type="radio"]') || [])].find(item => item.value === option.dataset.value);
    if (!input || input.disabled) return;
    dialog.close();
    input.click(); // Use the theme's original variant-selection and image-update path.
    scheduleSync();
  });
  close.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    const r = dialog.getBoundingClientRect();
    if (event.clientY < r.top || event.clientY > r.bottom || event.clientX < r.left || event.clientX > r.right) dialog.close();
  });
  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('jb-gem-sheet-open');
    requestAnimationFrame(() => owner()?.querySelector('.jb-gemstone-chooser')?.focus({ preventScroll: true }));
  });
  mobile.addEventListener('change', () => { if (dialog.open) dialog.close(); });
  document.addEventListener('change', scheduleSync);
  new MutationObserver(scheduleSync).observe(document.body, { childList: true, subtree: true });
  sync();
})();

/* JB-PHOTO-EXAMPLES-LINK-V1:2026-09-26 */
(() => {
  const marker = 'data-jb-photo-examples';
  let frame = 0;
  const sync = () => {
    frame = 0;
    for (const root of document.querySelectorAll('jb-pdp-configurator')) {
      const source = root.querySelector('[data-gemstone-row] a.learn-more[href]');
      const selected = root.querySelector('[data-jb-option-group="Gemstone"] input:checked');
      const name = selected?.dataset.jbOptionLabel || 'this stone';
      let url;
      try { url = source ? new URL(source.getAttribute('href'), location.origin) : null; } catch (_) { url = null; }
      // Reuse the actual gemstone destination; never derive a collection handle from a name.
      const valid = url && url.origin === location.origin && url.pathname.startsWith('/collections/');
      for (const notice of root.querySelectorAll('[data-jb-note-pending]')) {
        let link = notice.querySelector(`[${marker}]`);
        if (!valid) { link?.remove(); continue; }
        if (!link) {
          link = document.createElement('a');
          link.setAttribute(marker, '');
          link.className = 'jb-photo-examples-link';
          link.target = '_blank';
          link.rel = 'noopener';
          notice.append(link);
        }
        const text = `See ${name} in other designs →`;
        if (link.textContent !== text) link.textContent = text;
        if (link.getAttribute('href') !== url.href) link.setAttribute('href', url.href);
        link.setAttribute('aria-label', `${text} (opens in a new tab)`);
      }
    }
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(sync); };
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['href', 'checked'] });
  document.addEventListener('change', schedule);
  sync();
})();
